import {
  DeepReadonlyObject,
  KeyFunctionMap,
  RxCollection,
  RxCollectionCreator,
  RxDocument,
  RxJsonSchema,
} from "rxdb"
import {
  Identifier,
  IdentifierAssociationType,
  IdentifierList,
  IdentifierType,
} from "src/util/types/dataModels/identifier"

//region Types

export interface IdentifierCollectionMethods extends KeyFunctionMap {
  bulkInsertByType: (data: IdentifierList) => Promise<void>
  bulkInsertAndUpdateByType: (data: IdentifierList) => Promise<void>
  checkForIdentifier: (id: string, type: IdentifierAssociationType) => Promise<boolean>
  itemHasIdentifierType: (itemId: string, identifierType: IdentifierType) => Promise<boolean>
  locationHasIdentifierType: (
    locationId: string,
    identifierType: IdentifierType
  ) => Promise<boolean>
  getListOfIdentifiers: (
    id: string,
    type: IdentifierAssociationType
  ) => Promise<DeepReadonlyObject<Identifier>[]>
  getScannedIdentifier: (
    data: string,
    type: IdentifierType
  ) => Promise<RxDocument<Identifier> | null>
}

export type IdentifierCollection = RxCollection<Identifier, object, IdentifierCollectionMethods>

//endregion

/**
 * Schema for identifier collections.
 */
export const identifierSchema: RxJsonSchema<Identifier> = {
  title: "Identifier schema",
  description: "Database for containing the identifier values",
  version: 0,
  primaryKey: "id",
  type: "object",
  keyCompression: true,
  properties: {
    id: { type: "string", maxLength: 36, uniqueItems: true },
    created_dt: { type: "string", maxLength: 64 },
    updated_dt: { type: "string", maxLength: 64 },
    data: { type: "string", maxLength: 256 },
    type: { type: "string", enum: ["qr", "rfid", "barcode"] as IdentifierType[] },
    item: { ref: "item", type: "string", maxLength: 36 },
    area: { type: "string", maxLength: 36 }, // FK to areas
    location: { type: "string", maxLength: 36 }, // FK to locations
  },
  required: ["id", "data", "type"],
  indexes: ["updated_dt", "item", "area", "location"],
} as const

export const identifierCollectionMethods: IdentifierCollectionMethods = {
  async bulkInsertByType(this: IdentifierCollection, data: IdentifierList) {
    const flattenedData = Object.values(data).flat()
    await this.bulkInsert(flattenedData)
  },

  async bulkInsertAndUpdateByType(this: IdentifierCollection, data: IdentifierList): Promise<void> {
    const flattenedData = Object.values(data).flat()
    const insertResults = await this.bulkInsert(flattenedData)

    // Error status 409 is for conflict in data, which is what we need
    const dataToUpdate = insertResults.error
      .filter(
        (item) =>
          item.status === 409 && item.documentInDb?.updated_dt !== item.writeRow.document.updated_dt
      )
      .map((item) => item.writeRow.document)

    await this.bulkUpsert(dataToUpdate)
  },

  async checkForIdentifier(
    this: IdentifierCollection,
    id: string,
    type: IdentifierAssociationType
  ): Promise<boolean> {
    // Using "count" is faster than "findOne" since it doesn't need to access the document data.
    const identifierCount = await this.count().where(type).eq(id).exec()
    return Boolean(identifierCount)
  },

  async itemHasIdentifierType(
    this: IdentifierCollection,
    itemId: string, // Item which the identifier belongs to
    identifierType: IdentifierType // "qr" || "rfid" || "barcode"
  ): Promise<boolean> {
    const result = await this.findOne({
      selector: {
        item: itemId,
        type: identifierType,
      },
    }).exec()
    return Boolean(result)
  },

  async locationHasIdentifierType(
    this: IdentifierCollection,
    locationId: string, // Location which the identifier belongs to
    identifierType: IdentifierType // "qr" || "rfid" || "barcode"
  ): Promise<boolean> {
    const result = await this.findOne({
      selector: {
        location: locationId,
        type: identifierType,
      },
    }).exec()
    return Boolean(result)
  },

  async getListOfIdentifiers(
    this: IdentifierCollection,
    id: string,
    type: IdentifierAssociationType
  ): Promise<DeepReadonlyObject<Identifier>[]> {
    const identifiersDB = await this.find().where(type).eq(id).exec()
    return identifiersDB.map((doc: RxDocument<Identifier>) => doc.toJSON())
  },

  async getScannedIdentifier(
    this: IdentifierCollection,
    data: string,
    type: IdentifierType
  ): Promise<RxDocument<Identifier> | null> {
    return await this.findOne().where({ type, data }).exec()
  },
}

/**
 * A collection creator that can be passed to the database when setting up collections.
 */
export const identifierCollectionCreator: RxCollectionCreator = {
  schema: identifierSchema,
  statics: identifierCollectionMethods,
}
